import { useState, useEffect } from 'react';
import { Mail, Phone, Calendar, CalendarX } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuthValue } from '../../context/AuthContext';
import { useFetchDocuments } from '../../hooks/useFetchDocuments';
import { useDeleteDocument } from '../../hooks/useDeleteDocument';
import { buildCancellationWhatsappLink } from '../../utilities/whatsapp';
import { buildLocalDateTime, formatLocalDateDisplay } from '../../utilities/dateUtils';
import { Card, Badge, Skeleton, useToast } from '../../components/ui';
import AppointmentCard from '../../components/AppointmentCard/AppointmentCard';

const CANCEL_WINDOW_MS = 18000000; // 5 horas

function Avatar({ name }) {
  const initials = name
    ? name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase()
    : '?';
  return (
    <div
      className="w-16 h-16 rounded-full bg-primary flex items-center justify-center shrink-0"
      aria-hidden="true"
    >
      <span className="text-xl font-bold text-primary-foreground">{initials}</span>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center gap-4 py-12 text-center">
      <div className="w-14 h-14 rounded-full bg-bg-secondary flex items-center justify-center">
        <CalendarX size={24} className="text-text-muted" aria-hidden="true" />
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-text-primary">Nenhum agendamento encontrado</p>
        <p className="text-sm text-text-muted">Agende agora mesmo e garanta seu horário!</p>
      </div>
      <Link
        to="/"
        className="text-sm font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus rounded"
      >
        Agendar horário
      </Link>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <Card>
        <Card.Body>
          <div className="flex items-center gap-4">
            <Skeleton className="w-16 h-16 rounded-full" />
            <div className="flex flex-col gap-2 flex-1">
              <Skeleton className="h-5 w-2/5" />
              <Skeleton className="h-4 w-1/3" />
            </div>
          </div>
        </Card.Body>
      </Card>
      <Skeleton.Card />
    </div>
  );
}

export default function MyProfile() {
  const [name, setName] = useState('');
  const [birthday, setBirthday] = useState('');
  const [email, setEmail] = useState('');
  const [cellphone, setCellphone] = useState('');

  const { user } = useAuthValue();
  const { documents, loading, error } = useFetchDocuments('users', user.uid);
  const { documents: transactionsList } = useFetchDocuments('transactions', user.uid, true);
  const { deleteData } = useDeleteDocument();
  const toast = useToast();

  const dateNow = new Date().getTime();

  useEffect(() => {
    if (documents) {
      documents.forEach((e) => {
        setName(e.name || '');
        setEmail(e.email || '');
        setCellphone(e.cellphone || '');
        setBirthday(e.birthday ? formatLocalDateDisplay(e.birthday) : '');
      });
    }
  }, [documents]);

  useEffect(() => {
    localStorage.setItem('userId', user.uid);
  }, [user]);

  const handleCancelAppointment = async (e, appointment) => {
    e.preventDefault();
    try {
      const docId = appointment.id || appointment.tid;
      if (!docId) return;

      const deleted = await deleteData('transactions', docId);
      if (!deleted) {
        toast.error('Não foi possível cancelar. Tente novamente ou entre em contato pelo WhatsApp.');
        return;
      }

      if (appointment.lunch) return;

      const whatsappLink = buildCancellationWhatsappLink({
        ...appointment,
        tid: appointment.tid || appointment.id,
      });

      if (whatsappLink) {
        toast.success('Agendamento cancelado! Redirecionando para o WhatsApp...');
        setTimeout(() => { window.location.href = whatsappLink; }, 3000);
      }
    } catch (err) {
      toast.error('Ocorreu um erro. Tente novamente.');
    }
  };

  if (loading) return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <ProfileSkeleton />
    </div>
  );

  if (error) return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <Card>
        <Card.Body>
          <p className="text-sm text-danger-text">Erro ao carregar dados. Tente recarregar a página.</p>
        </Card.Body>
      </Card>
    </div>
  );

  const upcoming = (transactionsList || []).filter(
    (a) => dateNow <= (buildLocalDateTime(a.date, a.hour?.[0])?.getTime() ?? 0) - CANCEL_WINDOW_MS,
  );
  const history = (transactionsList || []).filter(
    (a) => dateNow > (buildLocalDateTime(a.date, a.hour?.[0])?.getTime() ?? 0) - CANCEL_WINDOW_MS,
  ).slice(0, 10);

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 flex flex-col gap-6">

      {/* Card de perfil */}
      <Card>
        <Card.Body>
          <div className="flex items-center gap-4">
            <Avatar name={name} />
            <div className="flex flex-col gap-0.5 min-w-0">
              <h1 className="text-xl font-bold text-text-primary truncate">{name || '—'}</h1>
              <Badge variant="primary" className="self-start">Cliente</Badge>
            </div>
          </div>
        </Card.Body>

        <Card.Footer>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
            {[
              { icon: Mail, label: 'Email', value: email },
              { icon: Phone, label: 'Celular', value: cellphone },
              { icon: Calendar, label: 'Nascimento', value: birthday },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="flex items-center gap-2">
                <Icon size={14} className="shrink-0 text-text-muted" aria-hidden="true" />
                <dt className="text-xs text-text-muted">{label}:</dt>
                <dd className="text-sm font-medium text-text-primary truncate">{value || '—'}</dd>
              </div>
            ))}
          </dl>
        </Card.Footer>
      </Card>

      {/* Próximos agendamentos */}
      <section aria-labelledby="upcoming-heading">
        <h2 id="upcoming-heading" className="text-lg font-semibold text-text-primary mb-3">
          Próximos agendamentos
          {upcoming.length > 0 && (
            <Badge variant="primary" className="ml-2">{upcoming.length}</Badge>
          )}
        </h2>
        {upcoming.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="flex flex-col gap-3">
            {upcoming.map((app) => (
              <AppointmentCard
                key={app.id}
                appointment={app}
                canCancel={true}
                onCancel={handleCancelAppointment}
              />
            ))}
          </div>
        )}
      </section>

      {/* Histórico */}
      {history.length > 0 && (
        <section aria-labelledby="history-heading">
          <h2 id="history-heading" className="text-lg font-semibold text-text-primary mb-3">
            Histórico
          </h2>
          <div className="flex flex-col gap-3">
            {history.map((app) => (
              <AppointmentCard
                key={app.id}
                appointment={app}
                canCancel={false}
                onCancel={handleCancelAppointment}
              />
            ))}
          </div>
        </section>
      )}

    </div>
  );
}
